/**
 * ARIA Technician Profile Modal Component
 * Version: 1.0.0
 *
 * Displays comprehensive technician profile in a modal/drawer
 */

import { useQuery, gql } from '@apollo/client';
import {
  X,
  User,
  Phone,
  Mail,
  Award,
  Calendar,
  Briefcase,
  Truck,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { clsx } from 'clsx';

// Full technician details query
const TECHNICIAN_DETAILS_QUERY = gql`
  query TechnicianDetails($id: ID!) {
    technician(id: $id) {
      id
      employeeId
      sytelinePartnerId
      firstName
      lastName
      fullName
      email
      phone
      zone {
        id
        name
        description
        region
      }
      primaryCsc
      defaultWarehouse
      vehicleCapacity
      maxDailyJobs
      employmentStatus
      isAvailable
      skills {
        skill {
          id
          name
          description
          category
        }
        proficiencyLevel
        certifiedAt
      }
      assignedJobs {
        id
        sytelineOrderNumber
        seNumber
        customerName
        city
        status
        scheduling {
          scheduledDate
          startTime
        }
      }
      todayJobs {
        id
        sytelineOrderNumber
        seNumber
        customerName
        city
        status
        scheduling {
          scheduledDate
          startTime
        }
      }
      outlookEmail
      createdAt
      updatedAt
    }
  }
`;

interface TechnicianProfileModalProps {
  technicianId: string;
  isOpen: boolean;
  onClose: () => void;
}

const statusConfig: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700' },
  INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-700' },
  ON_LEAVE: { label: 'On Leave', color: 'bg-yellow-100 text-yellow-700' },
  TERMINATED: { label: 'Terminated', color: 'bg-red-100 text-red-700' },
};

const proficiencyLabels: Record<number, string> = {
  1: 'Beginner',
  2: 'Intermediate',
  3: 'Proficient',
  4: 'Advanced',
  5: 'Expert',
};

const proficiencyColors: Record<number, string> = {
  1: 'bg-gray-200',
  2: 'bg-blue-200',
  3: 'bg-green-200',
  4: 'bg-purple-200',
  5: 'bg-yellow-200',
};

interface Skill {
  skill: {
    id: string;
    name: string;
    description?: string;
    category?: string;
  };
  proficiencyLevel: number;
  certifiedAt?: string;
}

interface AssignedJob {
  id: string;
  sytelineOrderNumber?: string;
  seNumber?: string;
  customerName?: string;
  city?: string;
  status: string;
  scheduling?: {
    scheduledDate?: string;
    startTime?: string;
  };
}

interface TechnicianData {
  id: string;
  employeeId?: string;
  sytelinePartnerId?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone?: string;
  zone?: {
    id: string;
    name: string;
    description?: string;
    region?: string;
  };
  primaryCsc?: string;
  defaultWarehouse?: string;
  vehicleCapacity?: string;
  maxDailyJobs?: number;
  employmentStatus: string;
  isAvailable: boolean;
  skills: Skill[];
  assignedJobs: AssignedJob[];
  todayJobs: AssignedJob[];
  outlookEmail?: string;
  createdAt: string;
  updatedAt: string;
}

export function TechnicianProfileModal({ technicianId, isOpen, onClose }: TechnicianProfileModalProps) {
  const { data, loading, error } = useQuery(TECHNICIAN_DETAILS_QUERY, {
    variables: { id: technicianId },
    skip: !isOpen || !technicianId,
  });

  if (!isOpen) return null;

  const technician = data?.technician as TechnicianData | undefined;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Technician Profile</h2>
            {technician && (
              <p className="text-sm text-gray-500">{technician.employeeId}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading technician profile...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-500 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Failed to load technician profile
              </div>
            </div>
          ) : technician ? (
            <div className="space-y-6">
              {/* Profile Header */}
              <section className="flex items-start gap-4">
                <div className="w-20 h-20 bg-brand-100 rounded-full flex items-center justify-center">
                  <span className="text-brand-700 font-bold text-2xl">
                    {technician.firstName[0]}{technician.lastName[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {technician.fullName}
                    </h3>
                    <span className={clsx('badge', statusConfig[technician.employmentStatus]?.color || 'bg-gray-100 text-gray-700')}>
                      {statusConfig[technician.employmentStatus]?.label || technician.employmentStatus}
                    </span>
                  </div>
                  <p className="text-gray-500">{technician.employeeId}</p>
                  <div className="flex items-center gap-4 mt-2">
                    {technician.isAvailable ? (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle className="w-4 h-4" />
                        Available
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-red-600">
                        <AlertCircle className="w-4 h-4" />
                        Not Available
                      </span>
                    )}
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Contact Information
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${technician.email}`} className="hover:text-brand-600">
                      {technician.email}
                    </a>
                  </div>

                  {technician.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${technician.phone}`} className="hover:text-brand-600">
                        {technician.phone}
                      </a>
                    </div>
                  )}

                  {technician.outlookEmail && technician.outlookEmail !== technician.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">Outlook: {technician.outlookEmail}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Work Assignment */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Work Assignment
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Zone</p>
                    <p className="text-gray-900 font-medium">
                      {technician.zone?.name || 'Not assigned'}
                    </p>
                    {technician.zone?.description && (
                      <p className="text-sm text-gray-500">{technician.zone.description}</p>
                    )}
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">Primary CSC</p>
                    <p className="text-gray-900">{technician.primaryCsc || 'Not set'}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">Default Warehouse</p>
                    <p className="text-gray-900">{technician.defaultWarehouse || 'Not set'}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-500 uppercase">Max Daily Jobs</p>
                    <p className="text-gray-900">{technician.maxDailyJobs || 'Not set'}</p>
                  </div>
                </div>
              </section>

              {/* Vehicle */}
              {technician.vehicleCapacity && (
                <section className="card p-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4" />
                    Vehicle
                  </h4>
                  <p className="text-gray-900">{technician.vehicleCapacity}</p>
                </section>
              )}

              {/* Skills & Certifications */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Skills & Certifications
                </h4>
                {technician.skills && technician.skills.length > 0 ? (
                  <div className="space-y-3">
                    {technician.skills.map((skillEntry, idx) => (
                      <div
                        key={skillEntry.skill?.id || idx}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {skillEntry.skill?.name || 'Unknown Skill'}
                          </p>
                          {skillEntry.skill?.category && (
                            <p className="text-xs text-gray-500">{skillEntry.skill.category}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map((level) => (
                              <div
                                key={level}
                                className={clsx(
                                  'w-2 h-4 rounded-sm',
                                  level <= skillEntry.proficiencyLevel
                                    ? proficiencyColors[skillEntry.proficiencyLevel]
                                    : 'bg-gray-200'
                                )}
                              />
                            ))}
                          </div>
                          <span className="text-xs text-gray-500 w-20">
                            {proficiencyLabels[skillEntry.proficiencyLevel] || `Level ${skillEntry.proficiencyLevel}`}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No skills recorded</p>
                )}
              </section>

              {/* Today's Jobs */}
              <section className="card p-4">
                <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Today's Schedule ({technician.todayJobs?.length || 0} jobs)
                </h4>
                {technician.todayJobs && technician.todayJobs.length > 0 ? (
                  <div className="space-y-2">
                    {technician.todayJobs.map((job) => (
                      <div
                        key={job.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {job.seNumber || job.sytelineOrderNumber}
                          </p>
                          <p className="text-sm text-gray-600">
                            {job.customerName} • {job.city}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {job.scheduling?.startTime
                              ? new Date(job.scheduling.startTime).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                              : 'TBD'}
                          </p>
                          <span className={clsx(
                            'text-xs capitalize px-2 py-0.5 rounded',
                            job.status === 'in_progress'
                              ? 'bg-purple-100 text-purple-700'
                              : job.status === 'completed'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-blue-100 text-blue-700'
                          )}>
                            {job.status.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No jobs scheduled for today</p>
                )}
              </section>

              {/* Upcoming Jobs */}
              {technician.assignedJobs && technician.assignedJobs.length > 0 && (
                <section className="card p-4">
                  <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Upcoming Jobs ({technician.assignedJobs.length})
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {technician.assignedJobs.slice(0, 10).map((job) => (
                      <div
                        key={job.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {job.seNumber || job.sytelineOrderNumber}
                          </p>
                          <p className="text-xs text-gray-500">
                            {job.customerName} • {job.city}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-600">
                            {job.scheduling?.scheduledDate
                              ? new Date(job.scheduling.scheduledDate).toLocaleDateString()
                              : 'Not scheduled'}
                          </p>
                        </div>
                      </div>
                    ))}
                    {technician.assignedJobs.length > 10 && (
                      <p className="text-sm text-gray-500 text-center pt-2">
                        +{technician.assignedJobs.length - 10} more jobs
                      </p>
                    )}
                  </div>
                </section>
              )}

              {/* Metadata */}
              <section className="text-xs text-gray-400 pt-4 border-t border-gray-100">
                <div className="flex justify-between">
                  {technician.sytelinePartnerId && (
                    <span>SyteLine ID: {technician.sytelinePartnerId}</span>
                  )}
                  <span>Created: {new Date(technician.createdAt).toLocaleDateString()}</span>
                </div>
              </section>
            </div>
          ) : null}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button onClick={onClose} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
